import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';
import { withDate } from '@lucid/utils';

import Heading from '../heading/heading';
import Box from '../box/box';
import Container from '../container/container';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Text from '../text/text';
import Link from '../link/link';
import type { Lucid } from '../../types/types';

export interface SimpleQuoteProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The author of the quote. */
  attribution: string;
  /** The date of the quote. */
  date?: string;
  /** Extra padding number to pass to the Container component */
  extraPadding?: number;
  /** Simple heading above the quote. */
  heading?: string;
  /** The logo of the company for the quote. */
  image?: Lucid.Directus.Generated.Directus_Files;
  /** Link after the quote attribution - structured like a link from Drupal */
  link?: Lucid.Directus.Generated.Links;
  /** Variant to be applied to Quote link */
  linkVariant?: Lucid.Theme.Component.Link.VariantKeys;
  /** The content of the quote. */
  quote: string;
  /** Determines whether or not the quote information should stack */
  shouldStackInfo?: boolean;
}

const SimpleQuote: FC<PropsWithChildren<SimpleQuoteProps>> = ({
  attribution,
  className,
  date,
  extraPadding,
  heading,
  image,
  link,
  linkVariant = 'primary',
  quote,
  shouldStackInfo,
}) => (
  <Container className={className} extraPadding={extraPadding}>
    <Box
      css={theme => ({
        label: 'simpleQuote',
        margin: `${theme.space.s40}px 0`,
        [theme.breakpoints.bp900.min]: {
          margin: `${theme.space.s104}px 0`,
        },
      })}
    >
      {heading && (
        <Heading as="h2" variant="headingXL">
          {heading}
        </Heading>
      )}
      <Box
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {image && image.filename_disk && (
          <Box
            css={theme => ({
              flex: '1 1 100%',
              marginBottom: theme.space.s32,
              [theme.breakpoints.bp768.min]: {
                flex: '0 1 25%',
              },
              [theme.breakpoints.bp1024.min]: {
                marginBottom: 0,
              },
            })}
          >
            <ImageWithPlaceholder
              alt={image.translations?.[0]?.alt || ''}
              css={{ maxHeight: '75px' }}
              src={withImage(image)}
            />
          </Box>
        )}
        <Box
          css={theme => ({
            flex: '1 1 100%',
            [theme.breakpoints.bp768.min]: {
              flex: `0 1 ${image ? '70%' : '100%'}`,
            },
          })}
        >
          <Text variant="paragraphXL300">
            <q>{quote}</q>
          </Text>
          <Box
            css={theme => ({
              display: 'flex',
              flexDirection: shouldStackInfo ? 'column' : 'row',
              alignItems: shouldStackInfo ? 'left' : 'center',
              flexWrap: 'wrap',
              marginTop: theme.space.s16,
            })}
          >
            <Text
              css={theme => [
                {
                  fontWeight: theme.fontWeights.fw500,
                },
                link && !shouldStackInfo && { marginRight: theme.space.s120 },
              ]}
              variant="paragraphM400"
            >
              -&nbsp;
              {attribution}
              {date && (
                <span
                  css={theme => ({
                    color: theme.colors.coolGrey[60],
                  })}
                >
                  &nbsp;|&nbsp;
                  {withDate.format(date, { format: 'MMMM DD YYYY' }).formattedDate}
                </span>
              )}
            </Text>
            {link && (
              <Link href={link.final_url} iconAnimation="navigational" variant={linkVariant}>
                {link.default_link_text}
              </Link>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  </Container>
);

export { SimpleQuote };
export default SimpleQuote;
